import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination,
  Paper, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Typography, IconButton, Chip, MenuItem,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import StoreIcon from '@mui/icons-material/Store';
import MonitorIcon from '@mui/icons-material/Monitor';
import PrimarySearchBar from '../../components/searchBar/searchbar';
import Layout from '../../components/layout/Layout';
import {
  getStores, addStore, updateStore, deleteStore, storeFlagOff,
} from '../../Redux/Actions/storeAction';
import { assignScreenToStore } from '../../Redux/Actions/screenAction';
import { getQboStatus } from '../../Redux/Actions/qboAction';
import QboCustomerSearch from '../../components/QboCustomerSearch';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import DescriptionIcon from '@mui/icons-material/Description';
import StoreDocuments from './StoreDocuments';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import MenuPriceEditor from '../storeportal/MenuPriceEditor';
import AdminPriceEditor from './AdminPriceEditor';
import StorePaymentDialog from './StorePaymentDialog';
import { api } from '../../api/api';
import toast from 'react-hot-toast';
import { matchStoreSearch } from '../../utils/matchStoreSearch';
import { useStoreContract } from '../../hooks/useStoreContract';
import StoreContractPanel from '../../components/store/StoreContractPanel';
import './store.css';

const formatPhoneDisplay = (digits) => {
  const d = (digits || '').replace(/\D/g, '').slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 10)}${d.length > 10 ? '-' + d.slice(10) : ''}`;
};

const storeToForm = (store) => ({
  name: store.name || '',
  tljNo: store.tljNo || '',
  screenCount: store.screenCount || '',
  address1: store.address1 || '',
  address2: store.address2 || '',
  town: store.town || '',
  state: store.state || '',
  zipcode: store.zipcode || '',
  storePhone: (store.storePhone || '').replace(/\D/g, ''),
  ownerName: store.ownerName || '',
  ownerPhone: store.ownerPhone || '',
  ownerEmail: store.ownerEmail || '',
  managerName: store.managerName || '',
  managerPhone: store.managerPhone || '',
  managerEmail: store.managerEmail || '',
  hqManagerName: store.hqManagerName || '',
  hqManagerPhone: store.hqManagerPhone || '',
  hqManagerEmail: store.hqManagerEmail || '',
  status: store.status || 'active',
  memo: store.memo || '',
  openDate: store.openDate ? store.openDate.split('T')[0] : '',
  contractStartDate: store.contractStartDate ? store.contractStartDate.split('T')[0] : '',
  paymentOption: store.paymentOption || 'monthly',
  pricingTier: store.pricingTier || 'tier1',
});

const emptyForm = {
  name: '', tljNo: '', screenCount: '',
  address1: '', address2: '', town: '', state: '', zipcode: '',
  storePhone: '',
  ownerName: '', ownerPhone: '', ownerEmail: '',
  managerName: '', managerPhone: '', managerEmail: '',
  hqManagerName: '', hqManagerPhone: '', hqManagerEmail: '',
  status: 'active', memo: '',
  openDate: '', contractStartDate: '', paymentOption: 'monthly',
  pricingTier: 'tier1',
};

// ── Screen container (single slot) ────────────────────────────────────────
function ScreenSlot({ screen, socketRef, onUnassign }) {
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotLoading, setScreenshotLoading] = useState(false);
  const isOnline = screen?.status === true;

  // Listen for screenshot_received events for this screen
  useEffect(() => {
    if (!socketRef?.current || !screen?.mac) return;
    const handler = ({ mac, image }) => {
      if (mac === screen.mac) {
        setScreenshot(image);
        setScreenshotLoading(false);
      }
    };
    socketRef.current.on('screenshot_received', handler);
    return () => socketRef.current?.off?.('screenshot_received', handler);
  }, [screen?.mac, socketRef]);

  const handleClearCache = () => {
    if (!screen?.mac || !socketRef?.current) return;
    socketRef.current.emit('admin_clear_cache', { mac: screen.mac });
  };

  const handleRequestScreenshot = () => {
    if (!screen?.mac || !socketRef?.current) return;
    setScreenshotLoading(true);
    socketRef.current.emit('admin_request_screenshot', { mac: screen.mac });
  };

  const handleRefresh = () => {
    if (!screen?.mac || !socketRef?.current) return;
    socketRef.current.emit('admin_request_screenshot', { mac: screen.mac });
  };

  return (
    <div className={`screen-container ${isOnline ? 'screen-container--online' : 'screen-container--offline'}`}>
      <div className="screen-container-header">
        <span className="screen-container-code">CODE: {screen.code}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span className={`screen-container-badge ${isOnline ? 'screen-container-badge--online' : 'screen-container-badge--offline'}`}>
            <span className="screen-container-badge-dot" />
            {isOnline ? 'Online' : 'Offline'}
          </span>
          <IconButton size="small" title="연결 해제" onClick={() => onUnassign(screen._id)}
            sx={{ color: '#bbb', p: 0.3, '&:hover': { color: '#e53935' } }}>
            <DeleteOutlineIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </div>
      </div>

      <div className="screen-container-info">
        <div className="screen-container-playlist">
          🎬 {screen.playlist?.name || '플레이리스트 없음'}
        </div>
        <div style={{ marginTop: 4 }}>
          {screen.device_details?.device_name || '기기명 없음'}
          {screen.device_details?.os_version ? ` · Android ${screen.device_details.os_version}` : ''}
        </div>
        <div>
          {screen.device_details?.width && screen.device_details?.height
            ? `${screen.device_details.width} × ${screen.device_details.height}`
            : ''}
          {screen.device_details?.ip ? `  IP: ${screen.device_details.ip}` : ''}
        </div>
      </div>

      <div className="screen-screenshot-area">
        {screenshotLoading && (
          <div className="screen-screenshot-placeholder">캡처 중...</div>
        )}
        {!screenshotLoading && screenshot && (
          <img src={`data:image/png;base64,${screenshot}`} alt="screenshot" />
        )}
        {!screenshotLoading && !screenshot && (
          <div className="screen-screenshot-placeholder">
            📷<br />스크린샷 없음
          </div>
        )}
      </div>

      <div className="screen-container-actions">
        <button className="sc-btn sc-btn--refresh" onClick={handleRefresh} disabled={!isOnline}>
          🔄 새로고침
        </button>
        <button className="sc-btn sc-btn--cache" onClick={handleClearCache} disabled={!isOnline}>
          🗑️ Clear Cache
        </button>
        <button className="sc-btn sc-btn--screenshot" onClick={handleRequestScreenshot} disabled={!isOnline || screenshotLoading}>
          📸 스크린샷
        </button>
      </div>
    </div>
  );
}

// ── Empty slot ─────────────────────────────────────────────────────────────
function EmptySlot({ onAssign }) {
  return (
    <div className="screen-empty-slot">
      <MonitorIcon sx={{ fontSize: 36, color: '#d0c8ff' }} />
      <span style={{ fontSize: 12, color: '#aaa' }}>비어있는 슬롯</span>
      <button className="screen-empty-slot-btn" onClick={onAssign}>
        + 스크린 연결
      </button>
    </div>
  );
}

// ── Screen Assign Picker ────────────────────────────────────────────────────
function AssignPicker({ open, onClose, availableScreens, onSelect }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>스크린 연결</DialogTitle>
      <DialogContent>
        {availableScreens.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            연결 가능한 스크린이 없습니다.
          </Typography>
        ) : (
          availableScreens.map(s => (
            <Box key={s._id}
              onClick={() => onSelect(s)}
              sx={{
                p: 1.5, mb: 1, border: '1.5px solid #e0e0e0', borderRadius: 2,
                cursor: 'pointer', '&:hover': { borderColor: '#551FFF', background: '#f5f3ff' }
              }}>
              <Typography fontWeight={700} fontSize={13}>CODE: {s.code}</Typography>
              <Typography fontSize={12} color="text.secondary">
                {s.device_details?.device_name || '기기명 없음'} · {s.mac}
              </Typography>
              <Typography fontSize={11} color="text.secondary">
                플레이리스트: {s.playlist?.name || '없음'}
              </Typography>
            </Box>
          ))
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ color: '#888' }}>닫기</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Store Screen Management Dialog ─────────────────────────────────────────
function ScreenManageDialog({ open, onClose, store, socketRef }) {
  const dispatch = useDispatch();
  const { screens } = useSelector(s => s.Screen);
  const [pickerOpen, setPickerOpen] = useState(false);

  const storeScreens = screens.filter(s =>
    String(s.store?._id || s.store) === String(store?._id)
  );
  const availableScreens = screens.filter(s => !s.store || s.store === null);

  const slotCount = store?.screenCount || 0;
  const slots = Array.from({ length: slotCount }, (_, i) => storeScreens[i] || null);

  const handleAssign = (screen) => {
    dispatch(assignScreenToStore(screen._id, store._id));
    setPickerOpen(false);
  };

  const handleUnassign = (screenId) => {
    dispatch(assignScreenToStore(screenId, null));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: '#551FFF', display: 'flex', alignItems: 'center', gap: 1 }}>
        <MonitorIcon />
        {store?.tljNo} 스크린 관리
        <Typography variant="body2" color="text.secondary" sx={{ ml: 1, fontWeight: 400 }}>
          ({storeScreens.length}/{slotCount} 연결됨)
        </Typography>
      </DialogTitle>
      <DialogContent>
        {slotCount === 0 ? (
          <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            점포에 설정된 화면 수가 없습니다. 점포 수정에서 화면 수를 입력하세요.
          </Typography>
        ) : (
          <div className="screen-grid">
            {slots.map((screen, i) =>
              screen ? (
                <ScreenSlot
                  key={screen._id}
                  screen={screen}
                  socketRef={socketRef}
                  onUnassign={handleUnassign}
                  onAssign={() => setPickerOpen(true)}
                />
              ) : (
                <EmptySlot key={`empty-${i}`} onAssign={() => setPickerOpen(true)} />
              )
            )}
          </div>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderColor: '#551FFF', color: '#551FFF' }}>닫기</Button>
      </DialogActions>

      <AssignPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        availableScreens={availableScreens}
        onSelect={handleAssign}
      />
    </Dialog>
  );
}

// ── Main Store component ───────────────────────────────────────────────────
function Store({ user, socketRef }) {
  const dispatch = useDispatch();
  const {
    stores, storeLoading, isStoreAdded, isStoreUpdated, isStoreDeleted, lastSavedStore,
  } = useSelector(s => s.Store);

  const [textVal, setTextVal] = useState('');
  const [filtered, setFiltered] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all'); // all, no_address, no_price, no_screen, qbo_synced
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [screenManageStore, setScreenManageStore] = useState(null);
  const [qboSearchOpen, setQboSearchOpen] = useState(false);
  const [menuPriceStore, setMenuPriceStore] = useState(null);
  const [paymentStore, setPaymentStore] = useState(null);
  const [documentStore, setDocumentStore] = useState(null);
  const qboConnected = useSelector(s => s.Qbo?.connected);

  // 어드민/디자이너: SubUser로 로그인한 경우 상위 계정(user.user) 기준으로 스토어 조회해야 다른 유저가 저장한 스토어도 보임
  const effectiveUserId = user?.user?._id || user?._id;

  const contract = useStoreContract({
    onStoreChange: (latest) => {
      setEditTarget((prev) =>
        prev && String(prev._id) === String(latest._id) ? { ...prev, ...latest } : prev);
      if (effectiveUserId) dispatch(getStores(effectiveUserId));
    },
  });

  // QBO 연결 상태 확인
  // 정산·QBO 는 admin 전용 (보안 X4) — 다른 역할은 호출하지 않는다
  useEffect(() => { if (user?.role === 'admin') dispatch(getQboStatus()); }, []);
  useEffect(() => {
    if (effectiveUserId) dispatch(getStores(effectiveUserId));
  }, [effectiveUserId]);

  useEffect(() => {
    setFiltered(applyFilters(stores, textVal, statusFilter));
  }, [stores, textVal, statusFilter]);

  useEffect(() => {
    if (isStoreDeleted) {
      dispatch(storeFlagOff());
      handleClose();
      return;
    }
    if (isStoreAdded && lastSavedStore) {
      setEditTarget(lastSavedStore);
      setForm(storeToForm(lastSavedStore));
      dispatch(storeFlagOff());
      if (effectiveUserId) dispatch(getStores(effectiveUserId));
      toast.success('점포가 추가되었습니다. 아래에서 계약서를 보낼 수 있습니다.');
      return;
    }
    if (isStoreUpdated && lastSavedStore) {
      setEditTarget(lastSavedStore);
      setForm(storeToForm(lastSavedStore));
      dispatch(storeFlagOff());
      toast.success('저장되었습니다.');
    }
  }, [isStoreAdded, isStoreUpdated, isStoreDeleted, lastSavedStore]);

  const applyFilters = (list, search, filter) => {
    let result = [...list];
    // 상태 필터
    if (filter === 'no_address') result = result.filter(s => !s.address1 && !s.zipcode);
    else if (filter === 'no_screen') result = result.filter(s => !s.screenCount || s.screenCount === 0);
    else if (filter === 'qbo_synced') result = result.filter(s => !!s.qboCustomerId);
    else if (filter === 'no_qbo') result = result.filter(s => !s.qboCustomerId);
    if (search) {
      result = result.filter((s) => matchStoreSearch(s, search));
    }
    return result;
  };

  const handleSearch = (val) => {
    setTextVal(val);
    setPage(0);
    setFiltered(applyFilters(stores, val, statusFilter));
  };

  const handleFilterChange = (f) => {
    setStatusFilter(f);
    setPage(0);
    setFiltered(applyFilters(stores, textVal, f));
  };

  const handleOpen = (store = null) => {
    if (store) {
      setEditTarget(store);
      setForm(storeToForm(store));
    } else {
      setEditTarget(null);
      setForm(emptyForm);
    }
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setEditTarget(null);
    setForm(emptyForm);
  };

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleZipcodeBlur = async () => {
    const zip5 = (form.zipcode || '').replace(/\D/g, '').slice(0, 5);
    if (!/^\d{5}$/.test(zip5)) return;
    try {
      const { data } = await api.get(`/zipcode/${zip5}`);
      if (data) {
        setForm(f => ({
          ...f,
          town: data.city || f.town,
          state: data.stateAbbr || data.state || f.state,
        }));
      }
    } catch (e) { /* lookup optional */ }
  };

  const handleSubmit = () => {
    const obj = { ...form, user: effectiveUserId, screenCount: Number(form.screenCount) };
    const nm = (obj.name || '').trim();
    const tlj = (obj.tljNo || '').trim();
    if (!nm && tlj) obj.name = tlj;
    if (!tlj && nm) obj.tljNo = nm;
    if (editTarget) {
      dispatch(updateStore(editTarget._id, obj));
    } else {
      dispatch(addStore(obj));
    }
  };

  const handleDelete = (id) => {
    dispatch(deleteStore(id));
    setDeleteConfirm(null);
  };

  return (
    <Layout title="Store" user={user}>
    <Box sx={{ p: 2 }}>
      {/* Search bar */}
      <Box sx={{ px: 0, py: 1, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
        <Box sx={{ flex: 1, minWidth: 160, background: 'white', borderRadius: '12px', px: 1 }}>
          <PrimarySearchBar value={textVal} onChange={e => handleSearch(e.target.value)} />
        </Box>
        {textVal && (
          <button
            onClick={() => handleSearch('')}
            style={{ height: 40, padding: '0 14px', borderRadius: '12px', border: 'none', background: '#eee', cursor: 'pointer', fontFamily: 'Open Sans' }}
          >초기화</button>
        )}
        <button
          onClick={() => handleOpen()}
          style={{ height: 40, padding: '0 16px', borderRadius: '12px', border: 'none', background: '#551FFF', color: 'white', cursor: 'pointer', fontFamily: 'Open Sans', fontWeight: 600 }}
        >+ 점포 추가</button>
      </Box>

      {/* 필터 칩 */}
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
        {[
          { key: 'all', label: '전체' },
          { key: 'no_address', label: '주소 없음', color: '#f59e0b' },
          { key: 'no_screen', label: '스크린 없음', color: '#ef4444' },
          { key: 'qbo_synced', label: 'QBO 연동', color: '#22c55e' },
          { key: 'no_qbo', label: 'QBO 미연동', color: '#888' },
        ].map(f => (
          <Chip key={f.key} label={f.label} size="small"
            variant={statusFilter === f.key ? 'filled' : 'outlined'}
            onClick={() => handleFilterChange(f.key)}
            sx={{
              cursor: 'pointer', fontSize: 11, fontWeight: 600,
              ...(statusFilter === f.key ? { background: f.color || '#551FFF', color: '#fff' } : {}),
            }} />
        ))}
      </Box>

      {/* 총 건수 + 페이지네이션 */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Typography sx={{ fontSize: 13, color: '#888', fontWeight: 600 }}>
          총 {filtered.length}개 점포 {statusFilter !== 'all' && `(필터: ${statusFilter})`}
        </Typography>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
          rowsPerPageOptions={[20, 50, 100, { label: `전체 (${filtered.length})`, value: filtered.length }]}
          labelRowsPerPage="표시 수:"
          sx={{ '& .MuiTablePagination-toolbar': { minHeight: 36 }, '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: 12 } }}
        />
      </Box>

      {/* Desktop Table */}
      <Box className="store-desktop-view">
        <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: '#f5f3ff' }}>
                <TableCell sx={{ fontWeight: 700 }}>이름</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>TLJ No</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>주소</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>대표</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>점장</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>HQ 담당</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>화면수</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>상태</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>관리</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(s => (
                <TableRow key={s._id} hover>
                  <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>{s.name || s.tljNo || '—'}</TableCell>
                  <TableCell>{s.tljNo}</TableCell>
                  <TableCell>
                    <div style={{ fontSize: 13 }}>{[s.address1, s.address2].filter(Boolean).join(', ') || s.address}</div>
                    <div style={{ fontSize: 11, color: '#888' }}>{[s.town, s.state, s.zipcode].filter(Boolean).join(', ')}</div>
                  </TableCell>
                  <TableCell>
                    <div>{s.ownerName}</div>
                    <div style={{ fontSize: 11, color: '#888' }}>{s.ownerPhone}</div>
                  </TableCell>
                  <TableCell>
                    <div>{s.managerName}</div>
                    <div style={{ fontSize: 11, color: '#888' }}>{s.managerPhone}</div>
                  </TableCell>
                  <TableCell>
                    <div>{s.hqManagerName}</div>
                    <div style={{ fontSize: 11, color: '#888' }}>{s.hqManagerPhone}</div>
                  </TableCell>
                  <TableCell>{s.screenCount}</TableCell>
                  <TableCell>
                    <Chip
                      label={s.status === 'active' ? '운영중' : '비활성'}
                      size="small"
                      sx={{ background: s.status === 'active' ? '#e8f5e9' : '#fce4ec', color: s.status === 'active' ? '#2e7d32' : '#c62828', fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" title="스크린 관리" onClick={() => setScreenManageStore(s)} sx={{ color: '#1565c0' }}>
                      <MonitorIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" title="메뉴 가격" onClick={() => setMenuPriceStore(s)} sx={{ color: '#1B5E3B' }}>
                      <RestaurantMenuIcon fontSize="small" />
                    </IconButton>
                    {user?.role === 'admin' && (
                      <IconButton size="small" title="결제·청구" onClick={() => setPaymentStore(s)} sx={{ color: '#C5A55A' }}>
                        <AttachMoneyIcon fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton size="small" title="서류 관리" onClick={() => setDocumentStore(s)} sx={{ color: '#FF6A00' }}>
                      <DescriptionIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" title="Quick Setup" onClick={() => window.location.href = `/quick-setup?storeId=${s._id}`} sx={{ color: '#9C27B0' }}>
                      <NoteAddIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleOpen(s)} sx={{ color: '#551FFF' }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => setDeleteConfirm(s._id)} sx={{ color: '#e53935' }}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4, color: '#aaa' }}>
                    {storeLoading ? '불러오는 중...' : '등록된 점포가 없습니다.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Mobile Cards */}
      <Box className="store-mobile-view">
        {filtered.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6, color: '#aaa' }}>
            {storeLoading ? '불러오는 중...' : '등록된 점포가 없습니다.'}
          </Box>
        )}
        {filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(s => (
          <div key={s._id} className="store-card">
            <div className="store-card-header">
              <div className="store-card-title">
                <StoreIcon sx={{ fontSize: 16, mr: 0.5, color: '#551FFF' }} />
                {s.tljNo}
              </div>
              <Chip
                label={s.status === 'active' ? '운영중' : '비활성'}
                size="small"
                sx={{ background: s.status === 'active' ? '#e8f5e9' : '#fce4ec', color: s.status === 'active' ? '#2e7d32' : '#c62828', fontWeight: 600, fontSize: 11 }}
              />
            </div>
            <div className="store-card-body">
              <div className="store-card-row"><span className="store-card-label">주소</span><span>{[s.address1, s.address2, s.town, s.state, s.zipcode].filter(Boolean).join(', ') || s.address}</span></div>
              <div className="store-card-row"><span className="store-card-label">매장 전화</span><span>{s.storePhone}</span></div>
              <div className="store-card-row"><span className="store-card-label">대표</span><span>{s.ownerName} / {s.ownerPhone}</span></div>
              <div className="store-card-row"><span className="store-card-label">점장</span><span>{s.managerName} / {s.managerPhone}</span></div>
              <div className="store-card-row"><span className="store-card-label">HQ 담당</span><span>{s.hqManagerName} / {s.hqManagerPhone}</span></div>
              <div className="store-card-row"><span className="store-card-label">화면수</span><span>{s.screenCount}</span></div>
            </div>
            <div className="store-card-actions">
              <button className="store-card-btn" onClick={() => setScreenManageStore(s)}
                style={{ background: '#e3f2fd', color: '#1565c0' }}>스크린</button>
              <button className="store-card-btn" onClick={() => setMenuPriceStore(s)}
                style={{ background: '#E8F5E9', color: '#1B5E3B' }}>메뉴</button>
              {user?.role === 'admin' && (
                <button className="store-card-btn" onClick={() => setPaymentStore(s)}
                  style={{ background: '#FFF8E1', color: '#B8860B' }}>결제</button>
              )}
              <button className="store-card-btn store-card-btn--edit" onClick={() => handleOpen(s)}>수정</button>
              <button className="store-card-btn store-card-btn--delete" onClick={() => setDeleteConfirm(s._id)}>삭제</button>
            </div>
          </div>
        ))}
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#551FFF' }}>
          {editTarget ? '점포 수정' : '점포 추가'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="점포명 (이름)" name="name" value={form.name} onChange={handleChange} size="small"
                placeholder="예: AL_Huntsville" helperText="계약서·검색에 사용됩니다" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="TLJ No" name="tljNo" value={form.tljNo} onChange={handleChange} size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="화면 수" name="screenCount" type="number" value={form.screenCount} onChange={handleChange} size="small" />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2" sx={{ color: '#551FFF', mb: 0.5 }}>주소 (집코드 입력 후 blur 시 City, State 자동 입력)</Typography>
                {qboConnected && (
                  <Button
                    size="small"
                    startIcon={<CloudDownloadIcon />}
                    onClick={() => setQboSearchOpen(true)}
                    sx={{ color: '#2CA01C', fontSize: 12, textTransform: 'none' }}
                  >
                    QuickBooks에서 가져오기
                  </Button>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Zip Code (집코드)" name="zipcode" value={form.zipcode} onChange={handleChange} onBlur={handleZipcodeBlur} size="small" placeholder="12345" inputProps={{ maxLength: 10 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Address 1 (주소 1)" name="address1" value={form.address1} onChange={handleChange} size="small" placeholder="Street address" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Address 2 (주소 2)" name="address2" value={form.address2} onChange={handleChange} size="small" placeholder="Suite, Unit, etc." />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="City (도시)" name="town" value={form.town} onChange={handleChange} size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="State (주)" name="state" value={form.state} onChange={handleChange} size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="매장 전화"
                name="storePhone"
                value={formatPhoneDisplay(form.storePhone)}
                onChange={(e) => setForm(f => ({ ...f, storePhone: (e.target.value || '').replace(/\D/g, '').slice(0, 11) }))}
                size="small"
                placeholder="(000) 000-0000"
                inputProps={{ inputMode: 'tel' }}
                sx={{ '& input': { letterSpacing: '0.02em' }, '& .MuiInputBase-input': { color: form.storePhone ? '#15192C' : '#999' } }}
                helperText="숫자만 입력됩니다"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select fullWidth label="상태" name="status" value={form.status} onChange={handleChange} size="small"
                SelectProps={{ native: true }}
              >
                <option value="active">운영중</option>
                <option value="inactive">비활성</option>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="오픈일" name="openDate" type="date" value={form.openDate} onChange={handleChange} size="small" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="계약 시작일" name="contractStartDate" type="date" value={form.contractStartDate} onChange={handleChange} size="small" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                select fullWidth label="결제 방식" name="paymentOption" value={form.paymentOption} onChange={handleChange} size="small"
              >
                <MenuItem value="monthly">월간</MenuItem>
                <MenuItem value="yearly">연간</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select fullWidth label="Pricing Tier" name="pricingTier" value={form.pricingTier || 'tier1'} onChange={handleChange} size="small"
              >
                <MenuItem value="tier1">Tier 1 (Small City)</MenuItem>
                <MenuItem value="tier2">Tier 2 (Mid City)</MenuItem>
                <MenuItem value="tier3">Tier 3 (Major City)</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12}><Typography variant="subtitle2" sx={{ color: '#551FFF', mt: 1 }}>대표 정보</Typography></Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="대표 이름" name="ownerName" value={form.ownerName} onChange={handleChange} size="small" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="대표 전화" name="ownerPhone" value={form.ownerPhone} onChange={handleChange} size="small" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="대표 이메일" name="ownerEmail" value={form.ownerEmail} onChange={handleChange} size="small" />
            </Grid>

            <Grid item xs={12}><Typography variant="subtitle2" sx={{ color: '#551FFF', mt: 1 }}>점장 정보</Typography></Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="점장 이름" name="managerName" value={form.managerName} onChange={handleChange} size="small" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="점장 전화" name="managerPhone" value={form.managerPhone} onChange={handleChange} size="small" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="점장 이메일" name="managerEmail" value={form.managerEmail} onChange={handleChange} size="small" />
            </Grid>

            <Grid item xs={12}><Typography variant="subtitle2" sx={{ color: '#551FFF', mt: 1 }}>HQ 담당자 정보</Typography></Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="HQ 담당자 이름" name="hqManagerName" value={form.hqManagerName} onChange={handleChange} size="small" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="HQ 담당자 전화" name="hqManagerPhone" value={form.hqManagerPhone} onChange={handleChange} size="small" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="HQ 담당자 이메일" name="hqManagerEmail" value={form.hqManagerEmail} onChange={handleChange} size="small" />
            </Grid>

            <Grid item xs={12}>
              <TextField fullWidth label="메모" name="memo" value={form.memo} onChange={handleChange} size="small" multiline rows={2} />
            </Grid>

            {editTarget && (
              <Grid item xs={12}>
                <StoreContractPanel store={editTarget} contract={contract} />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} sx={{ color: '#888' }}>취소</Button>
          <Button variant="contained" onClick={handleSubmit} sx={{ background: '#551FFF', borderRadius: 2 }}>
            {editTarget ? '저장' : '추가'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>점포 삭제</DialogTitle>
        <DialogContent>정말 삭제하시겠습니까?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>취소</Button>
          <Button color="error" variant="contained" onClick={() => handleDelete(deleteConfirm)}>삭제</Button>
        </DialogActions>
      </Dialog>

      {/* QBO Customer Search Modal */}
      <QboCustomerSearch
        open={qboSearchOpen}
        onClose={() => setQboSearchOpen(false)}
        onSelect={(data) => {
          setForm(f => ({
            ...f,
            ...data,
            // 기존 tljNo 등은 덮어쓰지 않음
            tljNo: f.tljNo || data.name || '',
          }));
        }}
      />

      {/* Screen Management Dialog */}
      {screenManageStore && (
        <ScreenManageDialog
          open={!!screenManageStore}
          onClose={() => setScreenManageStore(null)}
          store={screenManageStore}
          socketRef={socketRef}
        />
      )}

      <StorePaymentDialog
        open={!!paymentStore}
        onClose={() => setPaymentStore(null)}
        store={paymentStore}
        onApplied={() => effectiveUserId && dispatch(getStores(effectiveUserId))}
        onStorePatch={(patched) => {
          const o = patched?.toObject ? patched.toObject() : patched;
          setPaymentStore((prev) =>
            prev && o && String(o._id) === String(prev._id) ? { ...prev, ...o } : prev);
        }}
      />

      {/* Menu Price Dialog */}
      <Dialog open={!!menuPriceStore} onClose={() => setMenuPriceStore(null)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3, maxHeight: '90vh' } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1B5E3B', borderBottom: '3px solid #1B5E3B', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <span>Menu Prices — {menuPriceStore?.name || menuPriceStore?.tljNo}</span>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Button size="small" onClick={async () => {
              const tier = menuPriceStore?.pricingTier || 'tier1';
              try {
                const res = await api.post(`/menu-prices/init-tier/${menuPriceStore._id}`, { tier, userId: user._id });
                alert(res.data.message);
              } catch (e) { alert(e.response?.data?.message || e.message); }
            }} sx={{ fontSize: 11, color: '#C5A55A' }}>
              Init Tier
            </Button>
            <Button size="small" variant="outlined" onClick={async () => {
              try {
                const res = await api.post(`/svg/generate/${menuPriceStore._id}`);
                alert(`JPG 생성 완료: ${res.data?.screens?.length || 0}개 스크린`);
              } catch (e) { alert('JPG 생성 실패: ' + (e.response?.data?.message || e.message)); }
            }} sx={{ fontSize: 11, borderColor: '#551FFF', color: '#551FFF' }}>
              JPG 생성
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {menuPriceStore && (
            <Box sx={{ p: 2 }}>
              <AdminPriceEditor store={menuPriceStore} userId={user._id} />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Document Dialog */}
      <Dialog open={!!documentStore} onClose={() => setDocumentStore(null)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3, maxHeight: '90vh' } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#FF6A00', borderBottom: '3px solid #FF6A00' }}>
          서류 관리 — {documentStore?.name}
        </DialogTitle>
        <DialogContent sx={{ p: 2, pt: 2 }}>
          {documentStore && <StoreDocuments store={documentStore} />}
        </DialogContent>
      </Dialog>
    </Box>
    </Layout>
  );
}

export default Store;
